import React from 'react';
import type { TradeExecution } from '../../lib/types';
import { formatCurrency } from './formatters';

interface TradeExecutionsTableProps {
  trades: Array<TradeExecution & { userName: string }>;
  enabled: boolean;
}

const ExecutionRow: React.FC<{ trade: TradeExecution & { userName: string } }> = ({ trade }) => (
  <tr className="border-b border-gray-200 hover:bg-gray-50">
    <td className="px-4 py-3 text-sm text-gray-900">{trade.userName}</td>
    <td className="px-4 py-3 text-sm text-gray-900">{trade.symbol}</td>
    <td className="px-4 py-3 text-sm">
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          trade.side === 'Buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {trade.side}
      </span>
    </td>
    <td className="px-4 py-3 text-sm text-gray-900">{Number.parseFloat(trade.execQty).toFixed(4)}</td>
    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(Number.parseFloat(trade.execPrice))}</td>
    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(Number.parseFloat(trade.execValue))}</td>
    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(Number.parseFloat(trade.execFee))}</td>
    <td className="px-4 py-3 text-sm text-gray-600">{trade.isMaker ? 'Maker' : 'Taker'}</td>
    <td className="px-4 py-3 text-sm text-gray-600">{new Date(Number.parseInt(trade.execTime, 10)).toLocaleString()}</td>
  </tr>
);

export const TradeExecutionsTable: React.FC<TradeExecutionsTableProps> = ({ trades, enabled }) => {
  if (!enabled || trades.length === 0) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">All Trade Executions</h2>
        <p className="text-sm text-gray-600 mt-1">Individual executions including open trades, partial fills, and closed trades</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trader</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Side</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fee</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time (UTC)</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {trades
              .sort((a, b) => Number.parseInt(b.execTime, 10) - Number.parseInt(a.execTime, 10))
              .slice(0, 100)
              .map((trade, index) => (
                <ExecutionRow key={`${trade.execId}-${index}`} trade={trade} />
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
